/**
 * Session Cache
 *
 * Provides a cached, coalesced wrapper around NextAuth's `getSession()`.
 *
 * `getSession()` (from next-auth/react) always hits `/api/auth/session` —
 * it bypasses the SessionProvider cache. Without caching here, every API
 * call through the direct client triggers a redundant session fetch.
 *
 * We cache the session and only re-fetch when the access token is expiring
 * soon (within SESSION_CACHE_BUFFER_MS) or when the cache is empty.
 * Concurrent callers share a single in-flight fetch so N parallel React
 * Query calls produce only 1 request to `/api/auth/session`.
 */

import { getSession } from 'next-auth/react';

type SessionResult = Awaited<ReturnType<typeof getSession>>;

let cachedSession: SessionResult = null;
let sessionFetchPromise: Promise<SessionResult> | null = null;

/** Re-fetch session when token expires within this window (ms). */
const SESSION_CACHE_BUFFER_MS = 60_000;

function isSessionValid(session: SessionResult): boolean {
  if (!session?.accessToken) return false;
  if (!session.expiresAt) return false;
  const nowSec = Date.now() / 1000;
  return session.expiresAt - nowSec > SESSION_CACHE_BUFFER_MS / 1000;
}

/**
 * Get a (possibly cached) session. Concurrent callers share a single
 * in-flight fetch so N parallel React Query calls produce only 1 request.
 */
export async function getCachedSession(): Promise<SessionResult> {
  if (isSessionValid(cachedSession)) {
    return cachedSession;
  }

  // Coalesce concurrent callers onto one fetch
  if (!sessionFetchPromise) {
    sessionFetchPromise = getSession().finally(() => {
      sessionFetchPromise = null;
    });
  }

  cachedSession = await sessionFetchPromise;
  return cachedSession;
}

/** Force-clear the cache (e.g. after a token refresh). */
export function invalidateSessionCache() {
  cachedSession = null;
}
